import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  AuthOnFile,
  ClinicalDataEntry,
  MedicalPaOrder,
  PrimaryDiagnosisDetails,
} from "../data-model/medicalPaOrdersModel";
import { Submission } from "../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";

/**
 * Context value interface for the Initial Data of Single Medical Order.
 * Contains the initial snapshot of derived values captured at first load,
 * plus setter functions to feed data from external fetch hooks.
 *
 * @property {boolean} initialDataIsLoaded - Flag indicating if the initial data has been loaded
 * @property {string[]} [oldPrimaryIcdCodes] - Array of primary ICD codes from the initial load
 * @property {number} [countOfPrimaryIcdCodes] - Count of primary ICD codes from the initial load
 * @property {string} [oldAuthStatus] - The auth status from the initial load
 * @property {string} [oldRegimenIcdState] - The regimen ICD state from the initial submission load
 * @property {PrimaryDiagnosisDetails[]} [oldSubmissionDiagnoses] - Primary diagnosis details from the initial submission
 * @property {string} [currentOrderId] - The order ID for which data is currently captured
 * @property {function} setClinicalData - Updates the clinical data entries
 * @property {function} setAuthOnFileDetails - Updates the auth-on-file details
 * @property {function} setSubmissionData - Updates the submission data
 * @property {function} setOrderId - Updates the current order ID
 */
interface InitialDataOfSingleMedicalOrderProps {
  singleMedicalPaOrder?: MedicalPaOrder;
  initialDataIsLoaded: boolean;
  oldPrimaryIcdCodes?: string[];
  countOfPrimaryIcdCodes?: number;
  oldAuthStatus?: string;
  oldRegimenIcdState?: string;
  oldSubmissionDiagnoses?: PrimaryDiagnosisDetails[];
  currentOrderId?: string;
  setClinicalData: (data: ClinicalDataEntry[]) => void;
  setAuthOnFileDetails: (data: AuthOnFile) => void;
  setSubmissionData: (data: Submission) => void;
  setOrderId: (id: string) => void;
}

/**
 * Context for storing the initial state of a single medical PA order.
 * This context captures the order data on first load and preserves it,
 * allowing comparison with subsequent Redux state changes.
 */
const InitialDataOfSingleMedicalOrderContext =
  createContext<InitialDataOfSingleMedicalOrderProps>({
    singleMedicalPaOrder: undefined,
    initialDataIsLoaded: false,
    setClinicalData: () => {},
    setAuthOnFileDetails: () => {},
    setSubmissionData: () => {},
    setOrderId: () => {},
  });

/**
 * Custom hook to access the initial data of a single medical order.
 * Provides access to the initial snapshot of the order data, derived values,
 * and setter functions for feeding external data.
 *
 * @returns {InitialDataOfSingleMedicalOrderProps} The initial order data context value
 *
 * @example
 * const { oldAuthStatus, initialDataIsLoaded, setClinicalData } = useInitialDataOfSingleMedicalOrder();
 */
export const useInitialDataOfSingleMedicalOrder = () => {
  return useContext(InitialDataOfSingleMedicalOrderContext);
};

/**
 * Provider component that captures and stores the initial state of a single medical PA order.
 *
 * This provider:
 * - Subscribes to the Redux store for the single medical PA order
 * - Captures the order data only on the first load (when `initialDataIsLoaded` is false)
 * - Extracts and stores derived values (ICD codes, auth status) from the initial data
 * - Prevents subsequent Redux updates from overwriting the initial snapshot
 * - **Automatically resets** when the `orderId` changes (e.g., navigating to a different order)
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap with the provider
 * @returns {JSX.Element} Provider component wrapping children with initial order data context
 *
 * @example
 * <InitialDataOfSingleMedicalOrderProvider>
 *   <SingleOrderLayout />
 * </InitialDataOfSingleMedicalOrderProvider>
 */
export const InitialDataOfSingleMedicalOrderProvider = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const [clinicalData, setClinicalData] = useState<ClinicalDataEntry[]>([]);
  const [authOnFileDetails, setAuthOnFileDetails] = useState<
    AuthOnFile | undefined
  >(undefined);
  const [submissionData, setSubmissionData] = useState<Submission | undefined>(
    undefined,
  );
  const [orderId, setOrderId] = useState<string | undefined>(undefined);

  const [initialDataIsLoaded, setInitialDataIsLoaded] = useState(false);
  const [oldPrimaryIcdCodes, setOldPrimaryIcdCodes] = useState<string[]>([]);
  const [countOfPrimaryIcdCodes, setCountOfPrimaryIcdCodes] = useState(0);
  const [oldAuthStatus, setOldAuthStatus] = useState<string>("");
  const [oldRegimenIcdState, setOldRegimenIcdState] = useState<string>("");
  const [oldSubmissionDiagnoses, setOldSubmissionDiagnoses] = useState<
    PrimaryDiagnosisDetails[]
  >([]);
  const [currentOrderId, setCurrentOrderId] = useState<string | undefined>(
    undefined,
  );

  const previousOrderIdRef = useRef<string | undefined>(undefined);

  /**
   * Resets all context state to initial values.
   * Called when order ID changes to ensure fresh data capture.
   */
  const resetContextState = () => {
    setInitialDataIsLoaded(false);
    setOldPrimaryIcdCodes([]);
    setCountOfPrimaryIcdCodes(0);
    setOldAuthStatus("");
    setOldRegimenIcdState("");
    setOldSubmissionDiagnoses([]);
    setCurrentOrderId(undefined);
  };

  useEffect(() => {
    if (
      previousOrderIdRef.current !== undefined &&
      orderId !== undefined &&
      previousOrderIdRef.current !== orderId
    ) {
      resetContextState();
    }

    previousOrderIdRef.current = orderId;
  }, [orderId]);

  useEffect(() => {
    if (
      !initialDataIsLoaded &&
      orderId &&
      submissionData &&
      authOnFileDetails &&
      clinicalData
    ) {
      setCurrentOrderId(orderId);
      setOldPrimaryIcdCodes(
        submissionData?.primary_diagnosis_details?.map(
          (detail) => detail.icd_code ?? "",
        ) ?? [],
      );
      setCountOfPrimaryIcdCodes(
        submissionData?.primary_diagnosis_details?.length ?? 0,
      );
      setOldAuthStatus(authOnFileDetails?.auth_status ?? "");
      setOldRegimenIcdState(submissionData?.regimen_icd_state ?? "");
      setOldSubmissionDiagnoses(
        submissionData?.primary_diagnosis_details ?? [],
      );
      setInitialDataIsLoaded(true);
    }
  }, [
    clinicalData,
    authOnFileDetails,
    submissionData,
    orderId,
    initialDataIsLoaded,
    submissionData,
  ]);

  return (
    <InitialDataOfSingleMedicalOrderContext.Provider
      value={{
        initialDataIsLoaded,
        oldPrimaryIcdCodes,
        countOfPrimaryIcdCodes,
        oldAuthStatus,
        oldRegimenIcdState,
        oldSubmissionDiagnoses,
        currentOrderId,
        setClinicalData,
        setAuthOnFileDetails,
        setSubmissionData,
        setOrderId,
      }}
    >
      {children}
    </InitialDataOfSingleMedicalOrderContext.Provider>
  );
};
